import { useState, useEffect, useRef } from 'react';
import { formatNumber } from '../data/counterData';

const useCounter = (targetNumbers, duration = 3000) => {
  const [counters, setCounters] = useState(() => {
    const initialCounters = {};
    Object.keys(targetNumbers).forEach(key => {
      initialCounters[key] = 0;
    });
    return initialCounters;
  });

  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    // İlk yüklemede sayfa zaten görünür olabilir
    const checkInitialVisibility = () => {
      if (counterRef.current) {
        const rect = counterRef.current.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport && !hasStarted) {
          setIsVisible(true);
          setHasStarted(true);
          return true;
        }
      }
      return false;
    };

    // İlk kontrol
    if (checkInitialVisibility()) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setIsVisible(true);
          setHasStarted(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1, // %10 görünür olduğunda başla
        rootMargin: '0px 0px -50px 0px' // Alt kısımdan 50px önce başla
      }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    // Fallback: 1.5 saniye sonra eğer hala görünür değilse başlat
    const fallbackTimer = setTimeout(() => {
      if (!hasStarted) {
        setIsVisible(true);
        setHasStarted(true);
        observer.disconnect();
      }
    }, 1500);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, [hasStarted]);

  useEffect(() => {
    if (!isVisible) return; // Görünür değilse animasyonu başlatma

    const steps = 100; // 100 adım
    const stepDuration = duration / steps;

    const timer = setInterval(() => {
      setCounters(prevCounters => {
        const newCounters = { ...prevCounters };
        let allComplete = true;

        Object.keys(targetNumbers).forEach(key => {
          if (newCounters[key] < targetNumbers[key]) {
            // Küçük sayılar için daha küçük artış
            const increment = targetNumbers[key] <= 20 ? 1 : Math.ceil(targetNumbers[key] / steps);
            newCounters[key] = Math.min(newCounters[key] + increment, targetNumbers[key]);
            allComplete = false;
          }
        });

        if (allComplete) {
          clearInterval(timer);
        }

        return newCounters;
      });
    }, stepDuration);

    return () => clearInterval(timer);
  }, [targetNumbers, duration, isVisible]);

  // Formatlanmış sayıları döndür
  const formattedCounters = {};
  Object.keys(counters).forEach(key => {
    formattedCounters[key] = formatNumber(counters[key]);
  });

  return { counters: formattedCounters, counterRef };
};

export default useCounter;
